import { debounce } from '@/lib/lodash'
import { icons } from './icons'
import { Align, AlignCommand } from './types'

const getTargetBlock = (
  node: CKEDITOR.dom.element | CKEDITOR.dom.text
): CKEDITOR.dom.element | null => {
  const blockTags = {
    p: 1,
    div: 1,
    h1: 1,
    h2: 1,
    h3: 1,
    h4: 1,
    h5: 1,
    h6: 1,
    li: 1,
    td: 1,
    th: 1,
  }

  const block = node.getAscendant(
    // @ts-ignore
    blockTags,
    true
  ) as CKEDITOR.dom.element | null

  if (!block) return null

  // Если нашли блок внутри <li> — поднимаемся на <li>
  const parent = block.getParent() as CKEDITOR.dom.element | null
  if (parent && parent.getName() === 'li') {
    return parent
  }

  return block
}

const alignCommandConfig: Map<
  Align,
  {
    align: Align
    command: AlignCommand
    title: string
  }
> = new Map([
  [
    Align.LEFT,
    {
      align: Align.LEFT,
      command: AlignCommand.LEFT,
      title: 'По левому краю',
    },
  ],
  [
    Align.CENTER,
    {
      align: Align.CENTER,
      command: AlignCommand.CENTER,
      title: 'По центру',
    },
  ],
  [
    Align.RIGHT,
    {
      align: Align.RIGHT,
      command: AlignCommand.RIGHT,
      title: 'По правому краю',
    },
  ],
])

export const textAlignDropdownPlugin: CKEDITOR.pluginDefinition = {
  init: (editor) => {
    editor.addFeature({
      allowedContent: {
        'caption div h1 h2 h3 h4 h5 h6 p pre td th li': {
          propertiesOnly: true,
          styles: 'text-align',
          attributes: 'data-align',
        },
      },
    })

    let panelBlockEl: CKEDITOR.dom.element | null = null

    const getCurrentAlign = () => {
      const node = editor.getSelection()?.getRanges()?.[0].startContainer

      const targetBlock = getTargetBlock(node)

      const align = targetBlock?.getStyle('text-align') || Align.LEFT

      return align as Align
    }

    const onSelectionChange = debounce(
      () => {
        const textAlignDropdownButton = document
          .getElementById(`cke_${editor.name}`)
          ?.querySelector('.cke_button__textaligndropdown')

        textAlignDropdownButton?.setAttribute(
          'data-current-align',
          getCurrentAlign()
        )
      },
      100,
      { maxWait: 100 }
    )
    const onClickPanel = (eventInfo: CKEDITOR.eventInfo) => {
      const buttonEl = eventInfo.data
        .getTarget()
        ?.$?.closest('.js-text-align-dropdown-button') as HTMLElement | null

      const commandName = buttonEl?.dataset.command

      if (commandName) {
        editor.execCommand(commandName)
      }
    }

    for (const { align, command } of alignCommandConfig.values()) {
      editor.addCommand(command, {
        exec: () => {
          editor.focus()

          const selection = editor.getSelection()
          const ranges = selection?.getRanges()

          if (!ranges || ranges.length === 0) {
            return false
          }

          for (const range of ranges) {
            const iterator = range.createIterator()

            iterator.enforceRealBlocks = true

            let block = iterator.getNextParagraph()

            do {
              const targetBlock = getTargetBlock(block)

              if (align === 'left') {
                targetBlock?.removeStyle('text-align')
                targetBlock?.removeAttribute('data-align')
              } else {
                targetBlock?.setStyle('text-align', align)
                targetBlock?.setAttribute('data-align', align)
              }

              block = iterator.getNextParagraph()
            } while (block)
          }

          editor.fire('saveSnapshot')

          return false
        },
      })
    }

    editor.on('selectionChange', onSelectionChange)

    // @ts-ignore
    editor.ui.add('TextAlignDropdown', CKEDITOR.UI_PANELBUTTON, {
      label: 'Выравнивание',
      title: 'Выравнивание текста',
      // name: 'textaligndropdown',
      modes: {
        wysiwyg: 1,
      },
      editorFocus: false,
      toolbar: 'insert',

      panel: {
        css: [
          CKEDITOR.skin.getPath('editor'),
          editor.config.contentsCss,
          `${CKEDITOR.basePath}plugins/textaligndropdown.css`,
        ],
        attributes: { role: 'listbox' },
      },
      onOpen: () => {
        const buttonEls = panelBlockEl?.$.querySelectorAll<HTMLElement>(
          '.js-text-align-dropdown-button'
        )

        const currentAlign = getCurrentAlign()

        buttonEls?.forEach((buttonEl) => {
          const align = buttonEl.dataset.align as Align

          buttonEl.classList.toggle(
            'text-align-dropdown-panel__button_active',
            align === currentAlign
          )
        })
      },
      onBlock: (
        panel: {
          element: CKEDITOR.dom.element
          hide: () => void
        },
        block: {
          autoSize: boolean
          element: CKEDITOR.dom.element
        }
      ) => {
        panelBlockEl = block.element
        block.autoSize = false

        panel.element.addClass('cke_panel_textaligndropdown')
        block.element.addClass('text-align-dropdown-panel')

        let html = `
            <style>
                .text-align-dropdown-panel {
                    display: flex;
    
                }

                .text-align-dropdown-panel__button {
                    width: 28px;
                    height: 28px;
                    padding: 4px;
                    flex: 0 0 auto;
                    cursor: pointer;
                    box-sizing: border-box;
                }

                .text-align-dropdown-panel__button svg {
                    width: 100%;
                    height: 100%;
                    display: block;
                }

                .text-align-dropdown-panel__button_active {
                    background-color: #e0cbff;
                }

                .text-align-dropdown-panel__button:not(.text-align-dropdown-panel__button_active):hover {
                    background-color: rgb(55 71 79 / 7%);
                }

                .text-align-dropdown-panel__button_active svg {
                    fill: #673ab7;
                }
            </style>
        `

        for (const { command, align, title } of alignCommandConfig.values()) {
          html += `
                <div
                    data-command="${command}"
                    data-align="${align}"
                    title="${title}"
                    class="js-text-align-dropdown-button text-align-dropdown-panel__button"
                >
                    ${icons.get(align)}
                </div>
            `
        }

        block.element.setHtml(html)
        block.element.on('click', onClickPanel)
      },
    })

    editor.on('destroy', () => {
      onSelectionChange.cancel()
    })
  },
}
